<template>
  <div class="agent-upload-page">
    <div class="agent-upload-inner">
      <header class="page-header">
        <p class="eyebrow">After your certificate review</p>
        <h1>Help us improve your experience</h1>
        <p class="purpose-lead">
          Your feedback helps us understand your situation and improve the upload experience.
        </p>
      </header>

      <section class="purpose-panel">
        <div class="purpose-icon" aria-hidden="true">
          <i class="fas fa-comment-dots"></i>
        </div>
        <div class="purpose-body">
          <div class="analysis-summary">
            <h2>Analysis summary</h2>
            <p class="page-meta">
              {{ passedCount }} passed
              <template v-if="failedCount > 0"> · {{ failedCount }} need attention</template>
            </p>
          </div>

          <form @submit.prevent="submitFeedback">
            <div class="question-section">
              <h3>Application feedback</h3>

              <div class="question-card">
                <label for="appRating">How would you rate this application overall?</label>
                <div class="rating-input">
                  <div class="rating-stars">
                    <i
                      v-for="n in 5"
                      :key="n"
                      :class="['fas fa-star', { active: n <= answers.appRating }]"
                      @click="answers.appRating = n"
                    ></i>
                  </div>
                  <span class="rating-text">{{ getAppRatingText(answers.appRating) }}</span>
                </div>
              </div>

              <div class="question-card">
                <label for="wouldRecommend">Would you recommend this application to others?</label>
                <select id="wouldRecommend" v-model="answers.wouldRecommend" class="form-control" required>
                  <option value="">-- Select Recommendation --</option>
                  <option value="definitely">Definitely - I'd highly recommend it</option>
                  <option value="probably">Probably - It was helpful</option>
                  <option value="maybe">Maybe - It was okay</option>
                  <option value="probably-not">Probably not - Had some issues</option>
                  <option value="definitely-not">Definitely not - Poor experience</option>
                </select>
              </div>

              <div class="question-card">
                <label for="contactInterest">Would you like to be contacted to learn more about Praetorian AI?</label>
                <select id="contactInterest" v-model="answers.contactInterest" class="form-control" required>
                  <option value="">-- Select Interest Level --</option>
                  <option value="very-interested">Very interested - Please contact me soon</option>
                  <option value="somewhat-interested">Somewhat interested - Send me information</option>
                  <option value="maybe-later">Maybe later - Keep me on your list</option>
                  <option value="not-interested">Not interested at this time</option>
                </select>
              </div>

              <div class="question-card">
                <label for="additionalComments">Additional comments or suggestions (optional)</label>
                <textarea
                  id="additionalComments"
                  v-model="answers.additionalComments"
                  class="form-control"
                  rows="4"
                  placeholder="Tell us about your experience, what you liked, any issues you encountered, or suggestions for improvement..."
                ></textarea>
              </div>
            </div>

            <div class="action-buttons">
              <button
                type="submit"
                class="btn-primary"
                :disabled="!canSubmit || isSubmitting"
              >
                <i v-if="!isSubmitting" class="fas fa-paper-plane"></i>
                <i v-else class="fas fa-spinner fa-spin"></i>
                {{ isSubmitting ? 'Submitting...' : 'Submit feedback' }}
              </button>

              <button type="button" class="btn-link" @click="skipFeedback">
                Skip feedback
              </button>
            </div>
          </form>

          <div v-if="submitted" class="success-message">
            <i class="fas fa-check-circle"></i>
            <h4>Thank you for your feedback</h4>
            <p>Your responses help us improve our service and better assist future users.</p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'SecureUploadFeedback',
  data() {
    return {
      token: null,
      checkRunId: null,
      passedCount: 0,
      failedCount: 0,
      answers: {
        appRating: 0,
        wouldRecommend: '',
        contactInterest: '',
        additionalComments: '',
      },
      isSubmitting: false,
      submitted: false,
      error: null,
    };
  },

  computed: {
    canSubmit() {
      return this.answers.appRating > 0
        && this.answers.wouldRecommend
        && this.answers.contactInterest;
    },
  },

  created() {
    this.token = this.$route.params.token;
    this.checkRunId = this.$route.query.checkRunId;
    this.passedCount = parseInt(this.$route.query.passed, 10) || 0;
    this.failedCount = parseInt(this.$route.query.failed, 10) || 0;
  },

  methods: {
    async submitFeedback() {
      if (!this.canSubmit || this.isSubmitting) return;

      this.isSubmitting = true;
      this.error = null;

      try {
        await api.post(`/secure-upload/${this.token}/scenario-feedback/`, {
          check_run_id: this.checkRunId,
          app_rating: this.answers.appRating,
          would_recommend: this.answers.wouldRecommend,
          contact_interest: this.answers.contactInterest,
          additional_comments: this.answers.additionalComments,
          passed_count: this.passedCount,
          failed_count: this.failedCount,
          timestamp: new Date().toISOString(),
        });

        this.submitted = true;
        setTimeout(() => {
          this.proceedToFinal();
        }, 2000);
      } catch (err) {
        console.error('Error submitting feedback:', err);
        this.error = 'Failed to submit feedback. You can still proceed to the next step.';
      } finally {
        this.isSubmitting = false;
      }
    },

    skipFeedback() {
      this.proceedToFinal();
    },

    proceedToFinal() {
      this.$router.push({
        name: 'SecureUploadComplete',
        params: { token: this.token },
        query: {
          passed: this.passedCount,
          failed: this.failedCount,
        },
      });
    },

    getAppRatingText(rating) {
      const texts = {
        1: 'Poor',
        2: 'Fair',
        3: 'Good',
        4: 'Very Good',
        5: 'Excellent',
      };
      return texts[rating] || 'Rate the application';
    },
  },
};
</script>

<style scoped>
.agent-upload-page {
  min-height: 100vh;
  background: #f8fafc;
  color: #0f172a;
}

.agent-upload-inner {
  max-width: 40rem;
  margin: 0 auto;
  padding: 2rem 1.25rem 3rem;
}

.page-header {
  margin-bottom: 1.25rem;
}

.eyebrow {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin: 0 0 0.35rem;
}

.page-header h1 {
  margin: 0 0 0.5rem;
  font-size: 1.75rem;
  font-weight: 700;
}

.purpose-lead {
  margin: 0;
  color: #475569;
  line-height: 1.5;
}

.purpose-panel {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.85rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1rem 1.1rem;
}

.purpose-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.55rem;
  background: #f1f5f9;
  color: var(--p-accent, #1e3a5f);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.purpose-body {
  min-width: 0;
}

.analysis-summary {
  margin-bottom: 1.25rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid #e2e8f0;
}

.analysis-summary h2 {
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
}

.page-meta {
  margin: 0;
  font-size: 0.9rem;
  color: #475569;
  font-weight: 600;
}

.question-section h3 {
  margin: 0 0 0.75rem;
  font-size: 0.95rem;
}

.question-card {
  background: #f8fafc;
  border-radius: 0.5rem;
  padding: 0.85rem;
  margin-bottom: 0.65rem;
}

.question-card label {
  display: block;
  margin-bottom: 0.45rem;
  font-weight: 600;
  font-size: 0.875rem;
}

.form-control {
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  font-size: 0.9375rem;
}

.form-control:focus {
  outline: none;
  border-color: var(--p-accent, #1e3a5f);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--p-accent, #1e3a5f) 18%, transparent);
}

.rating-input {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.rating-stars {
  display: flex;
  gap: 0.2rem;
}

.rating-stars i {
  font-size: 1.25rem;
  color: #cbd5e1;
  cursor: pointer;
}

.rating-stars i.active {
  color: #d97706;
}

.rating-text {
  font-size: 0.875rem;
  color: #475569;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin-top: 1rem;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  width: 100%;
  padding: 0.7rem 1rem;
  background: var(--p-accent, #1e3a5f);
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.btn-link {
  background: none;
  border: none;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.35rem;
}

.btn-link:hover {
  color: var(--p-accent, #1e3a5f);
}

.success-message {
  margin-top: 1rem;
  padding: 1rem;
  background: #ecfdf5;
  border-radius: 0.5rem;
  color: #065f46;
  text-align: center;
}

.success-message i {
  font-size: 1.75rem;
  margin-bottom: 0.35rem;
  color: #047857;
}

.success-message h4 {
  margin: 0 0 0.35rem;
}

.success-message p {
  margin: 0;
  font-size: 0.9rem;
}
</style>
